import React, { useState } from "react";
import {
  downloadAndTranscribeCallById,
  analyzeCallById,
  downloadTranscription,
  getCallRecordingDownloadUrl,
} from "../../api";

export const LeadCard = ({ lead, clientId, initialCalls = [] }) => {
  const [calls, setCalls] = useState(initialCalls);
  const [showCalls, setShowCalls] = useState(false);
  const [transcribing, setTranscribing] = useState({});
  const [transcribedCalls, setTranscribedCalls] = useState({});
  const [analysisVisible, setAnalysisVisible] = useState({});

  const toggleShowCalls = () => {
    setShowCalls(!showCalls);
  };

  const toggleAnalysisVisibility = (noteId) => {
    setAnalysisVisible((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  const cleanPhoneNumber = (phoneText) => {
    if (!phoneText) return "Не указан";
    return phoneText.split(",")[0];
  };

  const handleDownload = (url, filename) => {
    if (!url) {
      console.error("URL для скачивания не предоставлен.");
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || ''); // Принудительное скачивание
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCall = (callId, filename) => {
    if (!callId) {
      console.error('ID звонка для скачивания не найден.');
      return;
    }
    const url = getCallRecordingDownloadUrl(callId);
    handleDownload(url, filename || `call_${callId}.mp3`);
  };

  const handleDownloadTranscription = (filename) => {
    if (!filename) {
      console.error("Имя файла для транскрипции не предоставлено");
      return;
    }
    const url = downloadTranscription(filename);
    handleDownload(url, filename);
  };

  const handleTranscribeCall = async (noteId) => {
    const callToTranscribe = calls.find((call) => call.id === noteId);
    if (!callToTranscribe || !callToTranscribe._id) {
      console.error(
        "Не удалось найти звонок или его MongoDB ID для транскрибации."
      );
      return;
    }

    const mongoId = callToTranscribe._id;

    setTranscribing((prev) => ({ ...prev, [noteId]: true }));
    try {
      const data = await downloadAndTranscribeCallById(mongoId);
      if (data.success) {
        setTranscribedCalls((prev) => ({ ...prev, [noteId]: data }));
        // Обновляем состояние звонков, чтобы скрыть кнопку после успешной транскрибации
        setCalls((currentCalls) =>
          currentCalls.map((c) =>
            c.id === noteId
              ? {
                  ...c,
                  filename_transcription: data.file_path,
                  transcription_status: "completed",
                }
              : c
          )
        );
      } else {
        console.error("Error transcribing call:", data.message);
      }
    } catch (err) {
      console.error("Error during transcription:", err);
    } finally {
      setTranscribing((prev) => ({ ...prev, [noteId]: false }));
    }
  };

  const formatCallDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-200">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{lead.name}</h3>
          <p className="text-sm text-gray-500">ID: {lead.id}</p>
        </div>

      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 text-sm">
        <div className="space-y-2">
          <p className="flex items-center">
            <span className="text-gray-500 min-w-[120px]">Создана:</span>
            <span className="text-gray-900">{lead.created_date}</span>
          </p>
          <p className="flex items-center">
            <span className="text-gray-500 min-w-[120px]">Ответственный:</span>
            <span className="text-gray-900">{lead.administrator || "Не указан"}</span>
          </p>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          {lead.calls_count > 0 && (
            <div className="mt-4">
              <button
                onClick={toggleShowCalls}
                className="gradient-button text-xs py-1.5 px-4"
              >
                {showCalls ? "Скрыть звонки" : `Показать звонки (${lead.calls_count})`}
              </button>
            </div>
          )}

          {showCalls && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-blue-600 mb-3">
                История звонков:
              </h4>

              {calls && calls.length > 0 ? (
                <ul className="space-y-3">
                  {calls.map((call) => {
                    const isTranscribed = transcribedCalls[call.id];

                    return (
                      <li
                        key={call._id}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Телефон: {cleanPhoneNumber(call.phone)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatCallDate(call.created_at)}
                            </p>
                          </div>
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${
                              call.direction === "inbound"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {call.direction === "inbound"
                              ? "Входящий"
                              : "Исходящий"}
                          </span>
                        </div>



                        <div className="mt-4">
                          {call.filename_transcription || isTranscribed ? (
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleDownloadTranscription(call.filename_transcription || isTranscribed.file_path)}
                                title="Скачать транскрипцию"
                                className="p-2 rounded-lg bg-gradient-to-br from-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              </button>
                              <button
                                onClick={() => handleDownloadCall(call._id, call.filename_audio)}
                                title="Скачать запись звонка"
                                className="p-2 rounded-lg bg-blue-500 text-white hover:opacity-90 transition-opacity"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                              </button>
                              {call.metrics && call.metrics.overall_score !== undefined && (
                                <button
                                  onClick={() => toggleAnalysisVisibility(call.id)}
                                  title="Показать/скрыть AI Анализ"
                                  className="p-2 rounded-lg bg-green-500 text-white hover:opacity-90 transition-opacity"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.572L16.5 21.75l-.398-1.178a3.375 3.375 0 00-2.456-2.456L12.5 17.25l1.178-.398a3.375 3.375 0 002.456-2.456L16.5 13.5l.398 1.178a3.375 3.375 0 002.456 2.456L20.5 17.25l-1.178.398a3.375 3.375 0 00-2.456 2.456z" /></svg>
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => handleTranscribeCall(call.id)}
                              disabled={transcribing[call.id]}
                              className="gradient-button text-white font-semibold rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-300 w-full flex items-center justify-center"
                            >
                              {transcribing[call.id] ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  <span>Транскрибация...</span>
                                </> 
                              ) : (
                                "Транскрибировать"
                              )}
                            </button>
                          )}
                        </div>

                        {isTranscribed &&
                          (isTranscribed.transcription_preview ||
                            isTranscribed.transcription_text) && (
                            <div className="mt-2 p-2 bg-gray-800 rounded text-gray-300 border border-gray-600">
                              <p className="text-xs font-medium mb-1 text-gray-400">
                                Предпросмотр транскрипции:
                              </p>
                              <p className="text-xs">
                                {isTranscribed.transcription_preview ||
                                  isTranscribed.transcription_text ||
                                  "Предпросмотр недоступен"}
                              </p>
                            </div>
                          )}

                        {analysisVisible[call.id] && call.metrics && (
                          <div className="mt-2 p-2 bg-gray-800 rounded text-gray-300 border border-green-900">
                            <p className="text-xs font-medium mb-1 text-green-400">
                              Результаты AI анализа:
                            </p>
                            {call.metrics.overall_score !== undefined && (
                              <div className="mb-2">
                                <p className="text-xs font-medium text-gray-400">
                                  Общая оценка:
                                </p>
                                <div className="relative mt-1 mb-4">
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>0</span>
                                    <span>10</span>
                                  </div>
                                  <div className="w-full bg-gray-700 rounded-full h-4 relative">
                                    <div
                                      className="bg-gradient-to-r from-red-500 to-green-500 h-4 rounded-full flex items-center justify-center"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          Math.max(
                                            0,
                                            call.metrics.overall_score * 10
                                          )
                                        )}%`,
                                      }}
                                    >
                                      {call.metrics.overall_score > 1 && (
                                        <span className="text-xs font-medium text-white relative z-10">
                                          {call.metrics.overall_score.toFixed(1)}
                                        </span>
                                      )}
                                    </div>
                                    {call.metrics.overall_score <= 1 && (
                                      <span className="text-xs font-medium text-white absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
                                        {call.metrics.overall_score.toFixed(1)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center p-4 bg-gray-700/30 rounded-lg border border-dashed border-gray-600">
                  <p className="text-gray-400">Нет доступных звонков</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCard;
